import { isSyncMetaKey, writeDisplayComment, writeDisplayReaction } from './cacheStore';
import {
  listTenantInfo,
  openDeviceStore,
  readDeviceMeta,
  writeDeviceMeta,
} from './deviceStore';
import {
  legacyCommentBlocksDeletion,
  legacyHazardBlocksDeletion,
  legacyQueueBlocksDeletion,
  migrateLegacyIntoUserStore,
  type LegacyCommentRow,
  type LegacyHazardRow,
  type LegacyQueueRow,
} from './legacyMigration';
import { closeLegacyHazardDatabase, openLegacyHazardDatabase, type LegacyHazardDatabase } from './legacyReader';
import type { UserStore } from './store';
import type { Reaction } from './types';

export const LEGACY_CLEANUP_STARTED_KEY = 'legacyCleanupStartedAt';
export const LEGACY_CLEANUP_MS = 30 * 24 * 60 * 60 * 1000;

export function shouldDeleteLegacyDatabase(
  blockingRows: number,
  cleanupStartedAt: number | null,
  now: number
): boolean {
  if (blockingRows === 0) {
    return true;
  }
  if (cleanupStartedAt == null || Number.isNaN(cleanupStartedAt)) {
    return false;
  }
  return now - cleanupStartedAt >= LEGACY_CLEANUP_MS;
}

export function countLegacyBlockers(input: {
  hazards: LegacyHazardRow[];
  comments: LegacyCommentRow[];
  queue: LegacyQueueRow[];
}): number {
  const hazards = input.hazards.filter(legacyHazardBlocksDeletion).length;
  const comments = input.comments.filter(legacyCommentBlocksDeletion).length;
  const queue = input.queue.filter(legacyQueueBlocksDeletion).length;
  return hazards + comments + queue;
}

function reactionRecord(reaction: Reaction): Record<string, unknown> {
  const record: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(reaction)) {
    if (value !== undefined) {
      record[key] = value;
    }
  }
  return record;
}

async function copyDeviceTables(legacy: LegacyHazardDatabase, host: string): Promise<void> {
  const device = await openDeviceStore(host);
  if ((await listTenantInfo(host)).length === 0) {
    const rows = await legacy.tenantInfo.toArray();
    if (rows.length > 0) {
      await device.tenantInfo.bulkPut(rows);
    }
  }
  if ((await device.functionAppVersion.count()) === 0) {
    const rows = await legacy.functionAppVersion.toArray();
    if (rows.length > 0) {
      await device.functionAppVersion.bulkPut(rows);
    }
  }
}

/** The lexicon is tenant vocabulary in the per-user store; copy it once when empty. */
async function copyLexicon(legacy: LegacyHazardDatabase, store: UserStore): Promise<void> {
  if ((await store.hazardLexicon.count()) === 0) {
    const rows = await legacy.hazardLexicon.toArray();
    if (rows.length > 0) {
      await store.hazardLexicon.bulkPut(rows);
    }
  }
}

async function copySyncMeta(legacy: LegacyHazardDatabase, store: UserStore): Promise<void> {
  const rows = await legacy.syncInfo.toArray();
  for (const row of rows) {
    if (!isSyncMetaKey(row.key)) {
      continue;
    }
    const existing = await store.meta.get(row.key);
    if (!existing) {
      await store.meta.put({ key: row.key, value: row.value });
    }
  }
}

async function mirrorDisplayCache(legacy: LegacyHazardDatabase, store: UserStore, userId: string): Promise<void> {
  const comments = await legacy.comments.toArray();
  for (const comment of comments) {
    if (!comment.id) {
      continue;
    }
    const pending = comment.last_synced == null || comment.local_only === true;
    if (pending && comment.user_id !== userId) {
      continue;
    }
    if (await store.comments.get(comment.id)) {
      continue;
    }
    const record: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(comment)) {
      if (value !== undefined) {
        record[key] = value;
      }
    }
    await writeDisplayComment(store, record);
  }
  const reactions = await legacy.reactions.toArray();
  for (const reaction of reactions) {
    const serverMirror = Boolean(reaction.last_synced) && reaction.local_only !== true && reaction.deleted !== true;
    const ownPending = reaction.user_id === userId && (reaction.local_only === true || !reaction.last_synced);
    if (!serverMirror && !ownPending) {
      continue;
    }
    if (await store.reactions.get(reaction.id)) {
      continue;
    }
    await writeDisplayReaction(store, reactionRecord(reaction));
  }
  const queued = await store.legacySyncQueue.toArray();
  for (const item of queued) {
    if (await store.reactions.get(item.reaction_id)) {
      continue;
    }
    const reaction = await legacy.reactions.get(item.reaction_id);
    if (!reaction) {
      continue;
    }
    await writeDisplayReaction(store, reactionRecord(reaction));
  }
}

/**
 * Copy whatever this user still needs out of HazardDatabase, then delete it
 * when nothing with migrated:false (or other unmigrated pending work) remains,
 * or when the cleanup clock has run for 30 days.
 */
export async function adoptLegacyDatabase(store: UserStore, userId: string, host: string): Promise<void> {
  const legacy = await openLegacyHazardDatabase();
  if (!legacy) {
    return;
  }
  let remove = false;
  try {
    await copyDeviceTables(legacy, host);
    await copyLexicon(legacy, store);
    await copySyncMeta(legacy, store);
    await migrateLegacyIntoUserStore(legacy, store, userId);
    await mirrorDisplayCache(legacy, store, userId);
    const blocking = countLegacyBlockers({
      hazards: await legacy.communityHazards.toArray(),
      comments: await legacy.comments.toArray(),
      queue: await legacy.syncQueue.toArray(),
    });
    const startedRaw = await readDeviceMeta(LEGACY_CLEANUP_STARTED_KEY, host);
    const startedAt = startedRaw == null ? null : Number(startedRaw);
    const now = Date.now();
    remove = shouldDeleteLegacyDatabase(blocking, startedAt, now);
    if (!remove && startedRaw == null) {
      await writeDeviceMeta(LEGACY_CLEANUP_STARTED_KEY, String(now), host);
    }
  } finally {
    await closeLegacyHazardDatabase(legacy, { deleteDatabase: remove });
  }
}
